import { useState } from "react";
import { api } from "@/lib/api";
import { isSuperAdmin } from "@/types";
import type { AuthUser, RoleInfo, User } from "@/types";
import { EditUserDialog } from "./EditUserDialog";

const ROLE_BADGE_COLORS: Record<string, string> = {
  super_admin: "bg-emerald-100 text-emerald-800",
  admin: "bg-blue-100 text-blue-800",
  manager: "bg-violet-100 text-violet-800",
  recruiter: "bg-cyan-100 text-cyan-800",
  screener: "bg-amber-100 text-amber-800",
  viewer: "bg-surface-secondary text-text-secondary",
  guest: "bg-stone-100 text-stone-600",
};

interface UserCardProps {
  user: User;
  currentUser: AuthUser;
  assignable: RoleInfo[];
  onRefresh: () => void;
}

export function UserCard({ user, currentUser, assignable, onRefresh }: UserCardProps) {
  const isSelf = user.id === currentUser.id;
  const canManage = !isSelf && assignable.some((r) => r.name === user.role);
  const canEdit = canManage && !isSuperAdmin(user);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const handleDelete = async () => {
    try {
      await api.deleteUser(user.id);
      onRefresh();
    } catch {
      alert("Failed to delete user");
    }
  };

  const badgeColor = ROLE_BADGE_COLORS[user.role] ?? "bg-surface-secondary text-text-secondary";

  return (
    <div className="flex items-center justify-between rounded-card bg-white px-5 py-4 shadow-card">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-near-black text-sm font-bold text-white">
          {user.username[0].toUpperCase()}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-near-black">{user.username}</span>
            {isSelf && <span className="text-[10px] font-medium text-text-secondary">(you)</span>}
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex rounded-badge px-2 py-0.5 text-[11px] font-semibold ${badgeColor}`}>
              {user.role_label ?? user.role}
            </span>
            {isSuperAdmin(currentUser) && user.org_name && (
              <span className="inline-flex rounded-badge bg-surface-secondary px-2 py-0.5 text-[11px] font-medium text-text-secondary">
                {user.org_name}
              </span>
            )}
          </div>
        </div>
      </div>

      {canManage && (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <button onClick={() => setShowEdit(true)} className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
              </svg>
              Edit
            </button>
          )}
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
              Delete
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button onClick={handleDelete} className="rounded-btn bg-rose-100 px-2.5 py-1 text-xs font-semibold text-error-text transition-colors hover:bg-rose-200">Confirm</button>
              <button onClick={() => setConfirmDelete(false)} className="rounded-btn px-2 py-1 text-xs font-medium text-text-secondary hover:text-near-black">Cancel</button>
            </div>
          )}
        </div>
      )}

      {showEdit && (
        <EditUserDialog
          user={user}
          assignable={assignable}
          isSuperAdmin={isSuperAdmin(currentUser)}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onRefresh(); }}
        />
      )}
    </div>
  );
}
